<script >
import { use } from '@fegobull/webapi';
import { computed, onMounted, ref } from 'vue-demi';
import { addWatchList, fetchGuessList, fetchGuessStats, removeWatchList, upsertGuessRecord } from './api';
import { useCommon, useFegoPageInfo } from './common';
import ClosePrice from './components/close-price.vue';
import StockFormModal from './components/stock-form-modal.vue';
import StockFormPopup from './components/stock-form-popup.vue';
import UpDown from './components/up-down.vue';
import { GuessType, LAYOUT } from './constants';
import locales from './locales';

// 说明：向宿主抛事件（用于埋点/兜底交互）
const __sfc_main = {};
__sfc_main.props = {
  // 活动标识
  actSign: {
    type: String,
    default: ''
  },
  // 展示样式
  layout: {
    type: Number,
    default: LAYOUT.FLAT
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const emit = __ctx.emit;
  const props = __props;
  const {
    kls,
    isPC
  } = useFegoPageInfo();
  const {
    toLogin
  } = useCommon();
  const {
    useToast,
    useI18n
  } = use;
  const Toast = useToast();
  const $t = useI18n(locales);

  // 场次唯一数据源：session_id -> session
  const sessionMap = ref({});
  // 原始场次 ID 列表（用于“下一场”等需要包含未来场次的逻辑）
  const sessionsRawIds = ref([]);
  // 展示用场次 ID 列表（按 show_start_time/show_end_time 过滤）
  const sessionsShowIds = ref([]);
  const records = ref([]);
  const balance = ref(0);
  const loading = ref(false);
  const formVisible = ref(false);
  // 自选操作状态：{ sessionId: boolean }
  const watchSubmittingMap = ref({});
  // 当前打开表单的场次（猜收盘价使用）
  const activeSessionId = ref('');
  const activeRecord = ref(null);
  // 每个场次的提交状态：{ sessionId: { submitting: boolean, errorMsg: string } }
  const submitStates = ref({});

  // 展示用 sessions（由 sessionsShowIds + sessionMap 映射得到）
  const sessions = computed(() => {
    const map = sessionMap.value || {};
    return (sessionsShowIds.value || []).map(id => map[id]).filter(Boolean);
  });

  // 原始 sessions（由 sessionsRawIds + sessionMap 映射得到）
  const sessionsRaw = computed(() => {
    const map = sessionMap.value || {};
    return (sessionsRawIds.value || []).map(id => map[id]).filter(Boolean);
  });

  // 当前弹窗场次：由 activeSessionId 从 sessionMap 派生，避免多份拷贝带来的同步问题
  const activeSession = computed(() => {
    const sid = String(activeSessionId.value || '');
    if (!sid) return null;
    return (sessionMap.value || {})[sid] || null;
  });

  // 下一场未开始的“猜收盘价”场次（用于截止态按钮展示）
  const nextClosePriceSession = computed(() => {
    const now = new Date();
    const list = (sessionsRaw.value || []).filter(s => Number(s?.type) === GuessType.ClosePrice && s?.show_start_time).map(s => {
      const d = new Date(s?.show_start_time);
      return {
        s,
        t: Number.isNaN(d.getTime()) ? 0 : d.getTime()
      };
    }).filter(x => x.t && x.t > now.getTime()).sort((a, b) => a.t - b.t);
    return list.length ? list[0].s : null;
  });

  // const layout = computed(() => {
  //     // 如果 sessions.length = 1，则强制使用平铺布局
  //     if (sessions.value.length === 1) {
  //         return LAYOUT.FLAT;
  //     }
  //     return props.layout;
  // });

  // 根据 sessionId 获取对应的竞猜记录
  const getRecordBySessionId = sessionId => {
    return records.value.find(record => record.session_id === sessionId) || null;
  };
  const getSessionById = sessionId => {
    const sid = String(sessionId || '');
    if (!sid) return null;
    return (sessionMap.value || {})[sid] || null;
  };
  const toValidDate = v => {
    if (!v) return null;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  };
  const isNotStartedSession = (s, now = new Date()) => {
    // 优先用后端 status（0-未开始）
    const st = Number(s?.status);
    if (Number.isFinite(st) && st === 0) return true;
    // 兜底用 start_time 判断
    const start = toValidDate(s?.start_time);
    if (start && now < start) return true;
    return false;
  };

  // 是否已超过展示结束时间（仅按 show_end_time 控制展示/请求）
  const isAfterShowEndSession = (s, now = new Date()) => {
    const end = toValidDate(s?.show_end_time);
    if (!end) return false;
    return now > end;
  };

  // 提交/修改后仅刷新当前场次的参与统计（避免全量刷新）
  const refreshSessionStats = async sessionId => {
    const sid = String(sessionId || '');
    if (!sid || !props.actSign) return;
    const map = sessionMap.value || {};
    const s = map[sid];
    if (!s) return;
    // 竞猜未开始的场次不需要统计
    if (isNotStartedSession(s)) return;
    try {
      const currentPrice = s?.quote?.nominalPrice != null ? Number(s.quote.nominalPrice) : undefined;
      const statsResp = await fetchGuessStats({
        actSign: props.actSign,
        sessions: [{
          sessionId: sid,
          currentPrice
        }]
      });
      const list = statsResp?.stats || [];
      const st = (list || []).find(x => String(x?.sessionId || '') === sid);
      if (!st) return;
      sessionMap.value = {
        ...map,
        [sid]: {
          ...s,
          participateCount: Number(st?.participateCount ?? 0) || 0,
          trendPercent: st?.trendPercent == null ? null : Number(st.trendPercent),
          trendDirection: st?.trendDirection === 'up' || st?.trendDirection === 'down' ? st.trendDirection : null,
          latestSubmitters: Array.isArray(st?.latestSubmitters) ? st.latestSubmitters : []
        }
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('刷新参与统计失败（忽略）', e);
    }
  };

  // 用接口返回的最新记录就地替换 records（避免提交后再 loadData 二次请求）
  const upsertLocalRecord = nextRecord => {
    const r = nextRecord && typeof nextRecord === 'object' ? nextRecord : null;
    const sid = r?.session_id != null ? String(r.session_id) : '';
    if (!sid) return;
    const idx = records.value.findIndex(item => String(item?.session_id ?? '') === sid);
    if (idx >= 0) {
      records.value.splice(idx, 1, r);
      return;
    }
    records.value.push(r);
  };

  // 获取场次的提交状态
  const getSubmitState = sessionId => {
    if (!submitStates.value[sessionId]) {
      submitStates.value[sessionId] = {
        submitting: false,
        errorMsg: ''
      };
    }
    return submitStates.value[sessionId];
  };

  // 提交竞猜
  const handleSubmitGuess = async (sessionId, submitData) => {
    const state = getSubmitState(sessionId);
    const record = getRecordBySessionId(sessionId);
    const session = getSessionById(sessionId);
    if (!session) {
      state.errorMsg = '场次不存在';
      return;
    }

    // 验证必填字段
    if (submitData.guessUpdown === undefined && submitData.guessPrice === undefined) {
      state.errorMsg = '请选择竞猜内容';
      return;
    }

    // 验证原因（如果需要）
    if (session.need_reason === 1 && (!submitData.reason || !submitData.reason.trim())) {
      state.errorMsg = '请填写竞猜原因';
      return;
    }

    // 验证价格（猜收盘价）
    if (submitData.guessPrice !== undefined) {
      const num = Number(submitData.guessPrice);
      if (!submitData.guessPrice || !Number.isFinite(num) || num <= 0) {
        state.errorMsg = '请输入有效的正数价格';
        return;
      }
    }
    state.submitting = true;
    state.errorMsg = '';
    try {
      const {
        record: nextRecord,
        balance: nextBalance
      } = await upsertGuessRecord({
        actSign: props.actSign,
        sessionId: sessionId,
        guessUpdown: submitData.guessUpdown,
        guessPrice: submitData.guessPrice,
        reason: submitData.reason?.trim() || undefined
      });

      // toast ‘已提交’
      Toast.msg($t('submitted'));
      if (nextRecord) {
        upsertLocalRecord(nextRecord);
        // 同步弹窗内的 record（用于回填）
        if (String(activeSessionId.value || '') === String(sessionId)) {
          activeRecord.value = nextRecord;
        }
      }
      if (nextBalance != null) balance.value = nextBalance;
      // 提交/修改成功后，刷新当前场次参与统计（不做阻塞）
      refreshSessionStats(sessionId);
    } catch (error) {
      console.error('提交竞猜失败:', error);
      state.errorMsg = error?.message || '提交失败，请重试';

      // toast ‘提交失败，请重试’
      Toast.msg($t('submitFailed'));
    } finally {
      state.submitting = false;
    }
  };

  // 过滤需要展示的竞猜场次
  const filterValidSessions = sessions => {
    const now = new Date();
    return sessions.filter(session => {
      // 说明：展示时间为空则不限制
      // - show_start_time：展示开始时间（now < show_start_time 不展示）
      // - show_end_time：展示结束时间（now > show_end_time 不展示）
      const showStart = toValidDate(session?.show_start_time);
      if (showStart && now < showStart) {
        return false;
      }
      const showEnd = toValidDate(session?.show_end_time);
      if (showEnd && now > showEnd) {
        return false;
      }
      return true;
    });
  };

  // 加载数据
  let loadReqSeq = 0;
  const loadData = async () => {
    if (!props.actSign) return;
    const reqId = ++loadReqSeq;
    loading.value = true;
    try {
      const result = await fetchGuessList({
        actSign: props.actSign
      });
      if (reqId !== loadReqSeq) return;
      const allSessions = result.sessions || [];
      // 建立 sessionMap 作为唯一数据源
      const baseMap = {};
      const rawIds = [];
      (allSessions || []).forEach(s => {
        const sid = String(s?.session_id || '');
        if (!sid) return;
        baseMap[sid] = s;
        rawIds.push(sid);
      });
      sessionMap.value = baseMap;
      sessionsRawIds.value = rawIds;
      sessionsShowIds.value = filterValidSessions(allSessions).map(s => String(s?.session_id || '')).filter(Boolean);
      records.value = result.records || [];
      balance.value = result.balance || 0;

      // 参与统计：独立接口返回，按 sessionId 合并到 sessionsRaw/sessions（用于展示文案）
      try {
        const statsReqSessions = (sessionsRaw.value || []
        // 竞猜未开始的场次不需要统计
        ).filter(s => !isNotStartedSession(s) && !isAfterShowEndSession(s)).map(s => ({
          sessionId: String(s?.session_id || ''),
          currentPrice: s?.quote?.nominalPrice != null ? Number(s.quote.nominalPrice) : undefined
        })).filter(x => Boolean(x.sessionId));
        const statsResp = await fetchGuessStats({
          actSign: props.actSign,
          sessions: statsReqSessions
        });
        if (reqId !== loadReqSeq) return;
        const list = statsResp?.stats || [];
        const statMap = new Map((list || []).map(x => ({
          sessionId: String(x?.sessionId || ''),
          participateCount: Number(x?.participateCount ?? 0) || 0,
          trendPercent: x?.trendPercent == null ? null : Number(x.trendPercent),
          trendDirection: x?.trendDirection === 'up' || x?.trendDirection === 'down' ? x.trendDirection : null,
          latestSubmitters: Array.isArray(x?.latestSubmitters) ? x.latestSubmitters : []
        })).filter(x => Boolean(x.sessionId)).map(x => [x.sessionId, x]));

        // 合并到 sessionMap（唯一数据源）
        const nextMap = {
          ...(sessionMap.value || {})
        };
        statMap.forEach((st, sid) => {
          const s = nextMap[sid];
          if (!s) return;
          nextMap[sid] = {
            ...s,
            participateCount: st.participateCount,
            trendPercent: st.trendPercent,
            trendDirection: st.trendDirection,
            latestSubmitters: st.latestSubmitters
          };
        });
        sessionMap.value = nextMap;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('获取参与统计失败（忽略）', e);
      }
    } catch (error) {
      console.error('加载竞猜数据失败:', error);
    } finally {
      if (reqId === loadReqSeq) loading.value = false;
    }
  };
  const handleFormSubmit = data => {
    // 说明：stock-form 只负责收集输入，这里统一走后端提交并刷新
    if (!activeSessionId.value) return;
    handleSubmitGuess(activeSessionId.value, data);
    formVisible.value = false;
  };
  const onClosePriceClick = payload => {
    if (!_params.uid) {
      toLogin();
      return;
    }

    // 如果是修改场景，且该竞猜场次修改需要消耗印花，且当前没有印花，toast 提示
    if (payload?.record && payload?.session?.modify_cost > 0 && Number(balance.value || 0) <= 0) {
      Toast.msg($t('noModifyChance'));
      emit('no-modify-chance', {});
      return;
    }
    const sessionId = payload?.sessionId || '';
    activeSessionId.value = sessionId;
    activeRecord.value = payload?.record || null;
    formVisible.value = true;
  };

  // 自选状态变更：同步 sessions 中对应场次的 isWatched
  const onWatchChange = payload => {
    const sessionId = String(payload?.sessionId || '');
    const isWatched = Boolean(payload?.isWatched);
    if (!sessionId) return;
    const map = sessionMap.value || {};
    if (!map[sessionId]) return;
    sessionMap.value = {
      ...map,
      [sessionId]: {
        ...map[sessionId],
        isWatched
      }
    };
  };
  const getWatchSubmitting = sessionId => {
    const sid = String(sessionId || '');
    if (!sid) return false;
    return Boolean(watchSubmittingMap.value?.[sid]);
  };

  // 自选点击：统一在父组件处理登录判断/接口调用/状态同步
  const onWatchClick = async payload => {
    const sessionId = String(payload?.sessionId || '');
    const stockId = String(payload?.stockId || '');
    const isWatched = Boolean(payload?.isWatched);
    if (!sessionId || !stockId) return;
    if (!_params.uid) {
      toLogin();
      return;
    }
    if (getWatchSubmitting(sessionId)) return;
    watchSubmittingMap.value = {
      ...(watchSubmittingMap.value || {}),
      [sessionId]: true
    };
    try {
      if (isWatched) {
        await removeWatchList(stockId);
        onWatchChange({
          sessionId,
          stockId,
          isWatched: false
        });
        Toast.msg($t('watchedRemoved'));
      } else {
        await addWatchList(stockId);
        onWatchChange({
          sessionId,
          stockId,
          isWatched: true
        });
        Toast.msg($t('watchedAdded'));
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('自选操作失败', e);
    } finally {
      watchSubmittingMap.value = {
        ...(watchSubmittingMap.value || {}),
        [sessionId]: false
      };
    }
  };
  onMounted(() => {
    loadData();
  });
  return {
    GuessType,
    kls,
    isPC,
    balance,
    loading,
    formVisible,
    activeRecord,
    sessions,
    activeSession,
    nextClosePriceSession,
    getRecordBySessionId,
    getSubmitState,
    handleSubmitGuess,
    handleFormSubmit,
    onClosePriceClick,
    onWatchClick
  };
};
__sfc_main.components = Object.assign({
  UpDown,
  ClosePrice,
  StockFormPopup,
  StockFormModal
}, __sfc_main.components);
export default __sfc_main;
</script>

<template>
    <div v-if="loading || sessions.length > 0" class="_guess-comp" :class="kls">
        <div
            v-for="session in sessions"
            :key="session.session_id"
            class="guess-session"
        >
            <!-- 猜涨跌类型 -->
            <UpDown
                v-if="session.type === GuessType.UpDown"
                :session="session"
                :record="getRecordBySessionId(session.session_id)"
                :act-sign="actSign"
                :submitting="getSubmitState(session.session_id).submitting"
                :error-msg="getSubmitState(session.session_id).errorMsg"
                @submit="(data) => handleSubmitGuess(session.session_id, data)"
            />

            <!-- 猜收盘价类型 -->
            <ClosePrice
                v-else-if="session.type === GuessType.ClosePrice"
                :session="session"
                :record="getRecordBySessionId(session.session_id)"
                :balance="balance || 0"
                :next-session="nextClosePriceSession"
                @click="onClosePriceClick"
                @watch-click="onWatchClick"
            />
        </div>

        <stock-form-popup
            v-if="formVisible && !isPC"
            :session="activeSession"
            :record="activeRecord"
            @close="formVisible = false"
            @submit="handleFormSubmit"
        />

        <stock-form-modal
            v-else-if="formVisible && isPC"
            :session="activeSession"
            :record="activeRecord"
            @close="formVisible = false"
            @submit="handleFormSubmit"
        />
    </div>
</template>

<style lang="less">
._guess-comp {
    width: 100%;

    .loading {
        text-align: center;
        padding: 20px;
        color: #999;
    }

    .guess-session {
        margin-bottom: 16px;

        &:last-child {
            margin-bottom: 0;
        }
    }
}
.cc-container[cc-drive-type="pc"] {
    position: relative !important;
    .base-task-item {
        text-align: left;
    }
}
</style>
